<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { __, SelfcareAccountSubscriberUsage, formatDuration, byteFormat, downloadCSV } from '@/utils';
import Table from '@components/table/table.vue';
import Loader from '@components/loader.vue';

const props = defineProps({
  displayedTables: Object,
  msisdn: String,
  selectedDate: String
});

const loader = ref(false);

const headings = [
  {
    id: 'call',
    headings: [
      __('date'),
      __('Time'),
      __('Tariff name'),
      __('Target'),
      __('Duration'),
      __('The amount to be charged, including VAT'),
    ],
    laber: __('Phone calls'),
    icon: `${theme_path.url}/resources/images/svg/call.svg`,
  },
  {
    id: 'sms',
    headings: [
      __('date'),
      __('Time'),
      __('Tariff name'),
      __('Target'),
      __('Quantity (units)'),
      __('The amount to be charged, including VAT'),
    ],
    laber: __('Messages'),
    icon: `${theme_path.url}/resources/images/svg/sms.svg`,
  },
  {
    id: 'data',
    headings: [
      __('date'),
      __('Time'),
      __('Tariff name'),
      __('Unit of measure'),
      __('Quantity (units)'),
      __('The amount to be charged, including VAT'),
    ],
    laber: __('Mobile browsing'),
    icon: `${theme_path.url}/resources/images/svg/data.svg`,
  },
];
const tableData = ref('');

const tables = computed(() => {
  const groupedData = tableData.value.reduce((acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  }, {});

  const getFooterTotal = (type) => {
    let total = 0;
    let formattedTotal = '';

    if(!groupedData[type]) {
      return formattedTotal;
    }

    switch (type) {
      case 'call':
        total = groupedData[type].reduce((acc, item) => {
          const durationParts = formatDuration(item.usage ).split(':').map(Number);
          const [hours, minutes, seconds] = durationParts;
          return acc + (hours * 3600 + minutes * 60 + seconds);
        }, 0);

        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        formattedTotal = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        break;

      case 'sms':
        total = groupedData[type].reduce((acc, item) => acc + parseFloat(item.usage), 0);
        formattedTotal = total.toString();
        break;

      case 'data':
        total = groupedData[type].reduce((acc, item) => acc + parseFloat(item.usage), 0);
        formattedTotal = byteFormat(total);
        break;
    }

    return formattedTotal;
  };

  const getFooterChargeTotal = (type) => {
    let total = 0;
    if(!groupedData[type]) {
      return total;
    }
    total = groupedData[type].reduce((acc, item) => acc + parseFloat(item.amount), 0);
    const formattedTotal = total.toFixed(2);

    return formattedTotal;
  };

  return headings.map((item) => ({
    id: item.id,
    headings: item.headings,
    label: item.laber,
    icon: item.icon,
    footers: [
      __('Total'),
      '', '', '',
      getFooterTotal(item.id),
    '₪' + getFooterChargeTotal(item.id),
    ],
    data: groupedData[item.id] || [],
  }));
});

const fetchUsage = async () => {
    let currentResponse;
    loader.value = true;
    try {
      const response = await SelfcareAccountSubscriberUsage(
        {
          msisdn: props.msisdn,
          date_range: props.selectedDate
        }
      );

      const responseData = JSON.parse(response.data);

      if (responseData?.response) {
        currentResponse = responseData.response;
      }
    } catch (error) {
      console.error('Error fetching payment URL:', error);
    }
    loader.value = false;

    return currentResponse;
};

const handleExport = (table) => downloadCSV(table);

onMounted(async () => {
  tableData.value = await fetchUsage();
});

watch([() => props.msisdn, () => props.selectedDate], async ([newMsisdn, newDate], [oldMsisdn, oldDate]) => {
  tableData.value = await fetchUsage();
});
</script>
<template>
  <Loader v-if="loader"  color="#2563eb" />
	<div class="account-main account-main--usages" v-if="tableData.length && !loader">
        <div v-for="table in tables" :key="table.id">
            <div class="heading heading--usages" v-if="displayedTables[table.id].checked">
                <img class="dp__icon" :src="table.icon"/>
                <strong>{{ table.label }}</strong>
                <button class="export-button" @click="handleExport(table)">
                  {{  __('Export to') }}
                  <img :src="`${theme_path.url}/resources/images/icon-export.svg`" alt="">
                </button>
            </div>
            <Table 
                :headings="table.headings"
                :footers="table.footers"
                :items="table.data"
                :visibleItems="25"
                v-if="displayedTables[table.id].checked"
            >
                <template #default="{ item, index }">
                    <td>{{ item.date }}</td>
                    <td>{{ item.time }}</td>
                    <td>{{ item.rate }}</td>
                    <td>{{ item.target }}</td>
                    <td v-if="table.id === 'call'">{{ formatDuration(item.usage) }}</td>
                    <td v-if="table.id === 'sms'">{{ item.usage }}</td>
                    <td v-if="table.id === 'data'">{{ byteFormat(item.usage, 2, false) }}</td>
                    <td>{{ item.amount }}</td>
                </template>
            </Table>
        </div>
    </div>
</template>